import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import LegalFooter from '@/components/LegalFooter';

const STEPS = ['experience', 'focus', 'session_length', 'goals', 'reminder'];

const FOCUS_OPTIONS = ['stress', 'anxiety', 'sleep', 'focus', 'self-love', 'gratitude', 'grief', 'creativity'];
const GOAL_OPTIONS = ['Build a daily habit', 'Sleep more peacefully', 'Lower anxiety', 'Sharpen focus', 'Be kinder to myself', 'Just explore'];

export default function Onboarding() {
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const [data, setData] = useState({
        experience_level: 'beginner',
        focus_areas: [],
        session_length: '5',
        goals: [],
        reminder_time: '08:00',
        timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
        community_optin: false,
    });

    const toggleArr = (key, value) => {
        setData((d) => {
            const set = new Set(d[key]);
            set.has(value) ? set.delete(value) : set.add(value);
            return { ...d, [key]: Array.from(set) };
        });
    };

    const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.put('/onboarding', data);
            await refresh();
            navigate('/app/home', { replace: true });
        } finally {
            setSubmitting(false);
        }
    };

    const stepKey = STEPS[step];

    return (
        <div className="min-h-screen bg-background relative overflow-hidden flex flex-col items-center justify-center px-6 py-10">
            <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-sage/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-[460px] h-[460px] rounded-full bg-lavender-soft/40 blur-3xl" />

            <div className="relative z-10 w-full max-w-xl qm-card p-8 md:p-10 animate-fade-in" data-testid="onboarding-card">
                {/* Progress dots */}
                <div className="flex items-center gap-2 mb-8">
                    {STEPS.map((_, i) => (
                        <div
                            key={i}
                            className={`h-1.5 rounded-full transition-all ${i <= step ? 'bg-forest w-8' : 'bg-forest/15 w-4'}`}
                        />
                    ))}
                    <span className="ml-auto text-xs text-forest-soft">Step {step + 1} of {STEPS.length}</span>
                </div>

                {stepKey === 'experience' && (
                    <Step title="How would you describe your practice?" subtitle="Be honest — there’s no wrong answer.">
                        <div className="grid gap-3" data-testid="step-experience">
                            {[
                                { v: 'beginner', label: 'Brand new', desc: 'I’m curious and just getting started.' },
                                { v: 'some', label: 'A little experience', desc: 'I’ve tried it on and off.' },
                                { v: 'experienced', label: 'It’s part of my life', desc: 'I practice regularly.' },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setData({ ...data, experience_level: o.v })}
                                    data-testid={`exp-${o.v}`}
                                    className={`text-left rounded-2xl border p-4 transition-all ${
                                        data.experience_level === o.v
                                            ? 'border-forest bg-sage/30 shadow-sm'
                                            : 'border-border hover:bg-sage/15'
                                    }`}
                                >
                                    <div className="font-medium text-forest">{o.label}</div>
                                    <div className="text-sm text-forest-soft">{o.desc}</div>
                                </button>
                            ))}
                        </div>
                    </Step>
                )}

                {stepKey === 'focus' && (
                    <Step title="What would you like to bring more of?" subtitle="Pick as many as you’d like.">
                        <div className="flex flex-wrap gap-2" data-testid="step-focus">
                            {FOCUS_OPTIONS.map((f) => {
                                const active = data.focus_areas.includes(f);
                                return (
                                    <button
                                        key={f}
                                        onClick={() => toggleArr('focus_areas', f)}
                                        data-testid={`focus-${f}`}
                                        className={`qm-pill ${active ? 'bg-forest text-cream border-forest' : 'border-forest/30 hover:bg-sage/30'}`}
                                    >
                                        {active && <Check size={14} className="inline -mt-0.5 mr-1" />}
                                        {f}
                                    </button>
                                );
                            })}
                        </div>
                    </Step>
                )}

                {stepKey === 'session_length' && (
                    <Step title="How long do you usually have?" subtitle="We’ll build your home around this.">
                        <div className="grid grid-cols-3 gap-3" data-testid="step-session-length">
                            {['3', '5', '10', '15', '20', '30'].map((m) => (
                                <button
                                    key={m}
                                    onClick={() => setData({ ...data, session_length: m })}
                                    data-testid={`length-${m}`}
                                    className={`rounded-2xl border py-4 transition-all ${
                                        data.session_length === m ? 'border-forest bg-sage/30' : 'border-border hover:bg-sage/15'
                                    }`}
                                >
                                    <div className="font-serif text-2xl text-forest">{m}</div>
                                    <div className="text-xs text-forest-soft">minutes</div>
                                </button>
                            ))}
                        </div>
                    </Step>
                )}

                {stepKey === 'goals' && (
                    <Step title="What brings you here?" subtitle="Choose what feels true today.">
                        <div className="flex flex-wrap gap-2" data-testid="step-goals">
                            {GOAL_OPTIONS.map((g) => {
                                const active = data.goals.includes(g);
                                return (
                                    <button
                                        key={g}
                                        onClick={() => toggleArr('goals', g)}
                                        data-testid={`goal-${g.toLowerCase().replace(/\s+/g, '-')}`}
                                        className={`qm-pill ${active ? 'bg-forest text-cream border-forest' : 'border-forest/30 hover:bg-sage/30'}`}
                                    >
                                        {active && <Check size={14} className="inline -mt-0.5 mr-1" />}
                                        {g}
                                    </button>
                                );
                            })}
                        </div>
                    </Step>
                )}

                {stepKey === 'reminder' && (
                    <Step title="A gentle daily nudge?" subtitle="No pressure — you can change this any time.">
                        <div className="space-y-4" data-testid="step-reminder">
                            <div>
                                <label className="text-sm text-forest-soft" htmlFor="reminder">Preferred time</label>
                                <input
                                    id="reminder" type="time"
                                    value={data.reminder_time}
                                    onChange={(e) => setData({ ...data, reminder_time: e.target.value })}
                                    className="qm-input mt-1"
                                    data-testid="reminder-time-input"
                                />
                            </div>
                            <label className="flex items-center gap-3 rounded-2xl border border-border p-4">
                                <input
                                    type="checkbox"
                                    checked={data.community_optin}
                                    onChange={(e) => setData({ ...data, community_optin: e.target.checked })}
                                    data-testid="community-optin"
                                    className="w-4 h-4 rounded text-forest"
                                />
                                <span className="text-sm text-forest">I’d like to join the community when it’s ready.</span>
                            </label>
                        </div>
                    </Step>
                )}

                <div className="mt-10 flex items-center justify-between">
                    <button
                        onClick={back}
                        disabled={step === 0}
                        data-testid="onboarding-back"
                        className="qm-btn-ghost text-sm disabled:opacity-40"
                    >
                        Back
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button onClick={next} data-testid="onboarding-next" className="qm-btn-primary inline-flex items-center gap-2">
                            Continue <ArrowRight size={16} />
                        </button>
                    ) : (
                        <button
                            onClick={submit}
                            disabled={submitting}
                            data-testid="onboarding-finish"
                            className="qm-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting ? 'Settling in …' : 'Take me in'} <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>
            <LegalFooter />
        </div>
    );
}

function Step({ title, subtitle, children }) {
    return (
        <div className="animate-fade-in">
            <h2 className="font-serif text-3xl text-forest leading-tight">{title}</h2>
            {subtitle && <p className="text-forest-soft mt-2">{subtitle}</p>}
            <div className="mt-6">{children}</div>
        </div>
    );
}
